// Google OAuth authorization code flow with PKCE. Refresh tokens allow
// silent token renewal. Session hard-capped at 7 days.
//
// Storage split:
//   sessionStorage ai_proto_token — short-lived id_token blob (per-tab, gone on close)
//   localStorage   ai_proto_refresh — refresh_token + 7-day anchor (survives tab close)
//
// On fresh tab open we re-hydrate sessionStorage from localStorage by
// exchanging the refresh_token for a new id_token — so the 7-day session
// actually works across tabs and browser restarts.
//
// Lifecycle helpers:
//   clearSession() — wipes in-memory + sessionStorage only. Safe to call on
//     transient failures (refresh 5xx, 401 retries) so the refresh_token
//     stays available for another attempt on the next tab open.
//   signOut()      — wipes everything including the refresh_token. Only call
//     on explicit user sign-out or when the session hard-cap has expired (at
//     which point the refresh_token is anchored to the same expired window
//     and can't produce a valid new id_token).
//
// XSS note: persisting a refresh_token in localStorage exposes it to any
// JS running on this origin; a single XSS bug would allow long-lived token
// theft across browser restarts. Accepted tradeoff for this internal
// Rewind-only tool (viewer content is public, the auth gate only controls
// who can *upload*, and the portal has a tiny XSS surface — no
// user-generated content rendering, no innerHTML of untrusted data, no
// eval). If this tool is ever opened up or extended to handle sensitive
// content, move to a BFF pattern: server-side refresh_token + httpOnly,
// Secure session cookie to the portal.

const STATE_KEY = "ai_proto_oauth_state";
const VERIFIER_KEY = "ai_proto_code_verifier";
const TOKEN_KEY = "ai_proto_token"; // sessionStorage
const REFRESH_KEY = "ai_proto_refresh"; // localStorage
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const REFRESH_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry

interface StoredSession {
  idToken: string;
  email: string;
  expiresAt: number; // ID token expiry (ms)
  sessionStartedAt: number; // hard cap anchor (ms)
}

interface RefreshEntry {
  refreshToken: string;
  sessionStartedAt: number;
}

let session: StoredSession | null = null;
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;

interface Config { googleClientId: string; apiUrl: string; }
let cfg: Config | null = null;

export async function initAuth(config: Config): Promise<void> {
  cfg = config;

  // Handle OAuth error (e.g. user denied consent) — only if we have an in-flight flow.
  // Without the state guard, any URL with ?error= would clear the user's session.
  // Use clearSession (not signOut): the user might have a still-valid refresh_token
  // from a prior grant, and a denied-consent here shouldn't destroy it.
  const params = new URLSearchParams(window.location.search);
  if (params.has("error") && sessionStorage.getItem(STATE_KEY)) {
    cleanUrl();
    clearSession();
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    return;
  }

  // Handle OAuth callback — exchange code for tokens
  const code = params.get("code");
  const state = params.get("state");
  if (code && state) {
    const savedState = sessionStorage.getItem(STATE_KEY);
    if (state !== savedState) throw new Error("OAuth state mismatch");

    const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
    if (!codeVerifier) throw new Error("Missing code verifier");

    const tokens = await exchangeCode(config.apiUrl, code, codeVerifier);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);

    const { email, exp } = parseIdToken(tokens.idToken);
    const now = Date.now();

    // Anchor resolution: if Google returned a new refresh_token, this is a
    // fresh grant — anchor to now. If not (common on repeat auth-code flows
    // without prompt=consent), preserve the existing localStorage anchor so
    // the 7-day hard cap is measured from the original grant, not from now.
    // With no refresh_token and no existing anchor, fall back to now — the
    // session can only live until id_token expiry in that case.
    const existingRefresh = loadRefresh();
    let sessionStartedAt: number;
    if (tokens.refreshToken) {
      sessionStartedAt = now;
      saveRefresh({ refreshToken: tokens.refreshToken, sessionStartedAt });
    } else if (existingRefresh && now <= existingRefresh.sessionStartedAt + SESSION_TTL_MS) {
      sessionStartedAt = existingRefresh.sessionStartedAt;
    } else {
      sessionStartedAt = now;
    }

    session = {
      idToken: tokens.idToken,
      email,
      expiresAt: Math.min(exp * 1000, sessionStartedAt + SESSION_TTL_MS),
      sessionStartedAt,
    };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));
    cleanUrl();
    scheduleRefresh();
    return;
  }

  // Restore from sessionStorage (same-tab reload)
  const stored = sessionStorage.getItem(TOKEN_KEY);
  if (stored) {
    try {
      // Pre-change sessions stored `refreshToken` inside the blob. Migrate
      // it to localStorage so upgraded users don't lose their session at
      // the first refresh attempt after this deploy.
      const parsed = JSON.parse(stored) as StoredSession & { refreshToken?: string };
      if (parsed.refreshToken && !loadRefresh()) {
        saveRefresh({
          refreshToken: parsed.refreshToken,
          sessionStartedAt: parsed.sessionStartedAt,
        });
      }

      const now = Date.now();
      if (now > parsed.sessionStartedAt + SESSION_TTL_MS) {
        signOut();
      } else {
        session = { ...parsed };
        delete (session as StoredSession & { refreshToken?: string }).refreshToken;
        if (session.expiresAt <= now + REFRESH_BUFFER_MS) {
          await doRefresh();
        } else {
          scheduleRefresh();
        }
      }
      return;
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      // fall through to localStorage re-hydrate
    }
  }

  // Fresh tab with no sessionStorage — try to re-hydrate from the refresh
  // token in localStorage. This is what makes the 7-day session persist
  // across tab-close / browser-restart.
  const refresh = loadRefresh();
  if (refresh) {
    const now = Date.now();
    if (now > refresh.sessionStartedAt + SESSION_TTL_MS) {
      clearRefresh();
    } else {
      await doRefresh();
    }
  }
}

export function isSignedIn(): boolean {
  if (!session) return false;
  const now = Date.now();
  if (now > session.sessionStartedAt + SESSION_TTL_MS) return false;
  return session.expiresAt > now + REFRESH_BUFFER_MS;
}

export function currentEmail(): string | null {
  return session?.email ?? null;
}

export function idToken(): string {
  if (!session) throw new Error("not signed in");
  return session.idToken;
}

/** Ensure the ID token is fresh before a critical operation like upload. */
export async function ensureFreshToken(): Promise<void> {
  if (!session) throw new Error("not signed in");
  const now = Date.now();
  if (now > session.sessionStartedAt + SESSION_TTL_MS) {
    // Past the 7-day hard cap — the refresh_token is anchored to the same
    // window and can't produce a valid new id_token either. Full signOut.
    signOut();
    throw new Error("session expired");
  }
  if (session.expiresAt <= now + REFRESH_BUFFER_MS) {
    await doRefresh();
  }
}

/**
 * Kick off the Google OAuth redirect.
 *
 * `prompt` is omitted by default so Google only shows consent UI on the
 * first-ever grant; returning users are redirected silently. Pass "none" for
 * silent re-auth after a session-expired 401. We intentionally do NOT default
 * to "consent" — that forces the "You're signing back in to <project>" screen
 * on every click and bleeds the GCP project name into the UI.
 *
 * Tradeoff: Google only re-issues a refresh_token when prompt=consent is set.
 * Without it, repeat auth-code flows return an id_token but no new
 * refresh_token. We compensate by persisting the first refresh_token in
 * localStorage (see REFRESH_KEY) so subsequent sessions can refresh silently
 * until the 7-day hard cap from the original grant.
 */
export async function signIn(prompt?: "none"): Promise<void> {
  if (!cfg) throw new Error("auth not initialized");

  const state = randomString(24);
  sessionStorage.setItem(STATE_KEY, state);

  const codeVerifier = randomString(64);
  sessionStorage.setItem(VERIFIER_KEY, codeVerifier);

  const codeChallenge = await sha256Base64Url(codeVerifier);

  const params = new URLSearchParams({
    client_id: cfg.googleClientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: window.location.origin + "/",
    state,
    hd: "rewind.io",
    access_type: "offline",
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (prompt) params.set("prompt", prompt);
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Clear in-memory and sessionStorage session only. Leaves the refresh_token
 * in localStorage so a subsequent tab open (or retry) can attempt to
 * rehydrate. Call this on transient failures (refresh 5xx, 401 from the
 * API) where the refresh_token might still be valid.
 */
export function clearSession(): void {
  session = null;
  if (refreshTimerId) { clearTimeout(refreshTimerId); refreshTimerId = null; }
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * Full sign-out: session + refresh_token. Only call when the user explicitly
 * signs out, or when we know the refresh_token can no longer produce a
 * usable session (past the 7-day hard cap).
 */
export function signOut(): void {
  clearSession();
  clearRefresh();
}

// --- internal ---

async function exchangeCode(apiUrl: string, code: string, codeVerifier: string) {
  const res = await fetch(`${apiUrl}/auth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "authorization_code",
      code,
      code_verifier: codeVerifier,
      // Must match the redirect_uri from the auth request (signIn) exactly —
      // the portal serves on two domains, so the server can't assume one.
      redirect_uri: window.location.origin + "/",
    }),
  });
  if (!res.ok) throw new Error("Token exchange failed");
  return (await res.json()) as { idToken: string; refreshToken?: string; expiresIn: number };
}

async function doRefresh(): Promise<void> {
  if (!cfg) { clearSession(); return; }
  const refresh = loadRefresh();
  if (!refresh) { clearSession(); return; }

  const now = Date.now();
  if (now > refresh.sessionStartedAt + SESSION_TTL_MS) {
    // Hard cap reached — the refresh_token is useless from here on.
    signOut();
    return;
  }

  // Preserve the refresh_token on any failure (network error, 5xx, or even
  // an ambiguous 400). Our token-exchange Lambda currently returns a generic
  // 400 for every failure, so we can't reliably distinguish "refresh_token
  // is invalid" from "transient upstream hiccup". Keeping the refresh_token
  // around means the next tab open (or the next ensureFreshToken) gets a
  // chance to retry; the cost of a false retain is that an actually-revoked
  // token will fail again on every attempt until the user explicitly signs
  // out. If the server grows a clearer invalid-grant signal we can upgrade
  // this to signOut() on that specific response.
  try {
    const res = await fetch(`${cfg.apiUrl}/auth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refresh.refreshToken,
      }),
    });
    if (!res.ok) { clearSession(); return; }
    const data = (await res.json()) as { idToken: string; expiresIn: number };

    const { email, exp } = parseIdToken(data.idToken);
    const hardCap = refresh.sessionStartedAt + SESSION_TTL_MS;
    session = {
      idToken: data.idToken,
      email,
      expiresAt: Math.min(exp * 1000, hardCap),
      sessionStartedAt: refresh.sessionStartedAt,
    };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));
    scheduleRefresh();
  } catch {
    clearSession();
  }
}

function scheduleRefresh(): void {
  if (refreshTimerId) clearTimeout(refreshTimerId);
  if (!session) return;
  const delay = session.expiresAt - Date.now() - REFRESH_BUFFER_MS;
  if (delay <= 0) return; // already due — caller should doRefresh() instead
  refreshTimerId = setTimeout(() => doRefresh(), delay);
}

function saveRefresh(entry: RefreshEntry): void {
  try { localStorage.setItem(REFRESH_KEY, JSON.stringify(entry)); } catch { /* quota/disabled */ }
}

function loadRefresh(): RefreshEntry | null {
  try {
    const raw = localStorage.getItem(REFRESH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RefreshEntry;
    if (typeof parsed?.refreshToken !== "string" || typeof parsed?.sessionStartedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearRefresh(): void {
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* disabled */ }
}

function parseIdToken(token: string): { email: string; exp: number } {
  const parts = token.split(".");
  if (parts.length < 2) throw new Error("bad id_token");
  let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  const payload = JSON.parse(atob(b64));
  return { email: payload.email as string, exp: payload.exp as number };
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(hash);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "").slice(0, len);
}

function cleanUrl(): void {
  history.replaceState(null, "", window.location.pathname);
}
